import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check for required environment variables
const hasSupabaseConfig = supabaseUrl && supabaseAnonKey;

if (!hasSupabaseConfig) {
  console.warn('Supabase environment variables not configured - Supabase auth will be disabled');
}

// Create Supabase client only if config exists, otherwise create a mock
export const supabase = hasSupabaseConfig 
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false
      }
    })
  : {
      auth: {
        signInWithPassword: async () => ({ 
          data: null, 
          error: { message: 'Supabase not configured. Please use Demo Mode.' } 
        }),
        signOut: async () => ({ error: null }),
        getSession: async () => ({ data: { session: null }, error: null })
      },
      from: () => ({
        select: () => ({
          eq: () => ({
            single: async () => ({ data: null, error: { message: 'Supabase not configured' } })
          })
        })
      })
    };

// Export flag for checking Supabase availability
export const isSupabaseConfigured = hasSupabaseConfig;

// Helper function to get the current user's session
export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return null;
  }
  return session;
};

// Helper function to get user profile from users table by ID
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.log('Profile fetch by ID error:', error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.error('Error fetching user profile:', err);
    return null;
  }
};

// Helper function to get user profile from users table by email
export const getUserProfileByEmail = async (email) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();
    
    if (error) {
      console.log('Profile fetch by email error:', error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.error('Error fetching user profile by email:', err);
    return null;
  }
};

// Helper function to sign in with email and password
export const signInWithEmail = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) {
    throw new Error(error.message);
  }
  return data;
};

// Helper function to sign up with email and password
export const signUpWithEmail = async (email, password, metadata = {}) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata
    }
  });
  
  if (error) {
    throw error;
  }
  return data;
};

// Helper function to sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw error;
  }
};

export default supabase;
